import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listCustomEncounterTypes,
  createCustomEncounterType,
} from "../../../../../services/api-settings";
import { BUILTIN_ENCOUNTER_TYPES } from "../../../../../utils/types";

export type NoteType = string;

export function useEncounterTypes(session: any) {
  const [custom, setCustom] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clinicId = session?.clinicId;

  const load = useCallback(async () => {
    if (!session || !clinicId) {
      setCustom([]);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const data = await listCustomEncounterTypes(session, clinicId);
      const names = (data || [])
        .map((t: any) => t?.name?.trim())
        .filter((n: string | undefined): n is string => !!n);
      setCustom(names);
      setError(null);
    } catch (e: any) {
      setError("Unable to load note types.");
    } finally {
      setLoading(false);
    }
  }, [session, clinicId]);

  useEffect(() => {
    load();
  }, [load]);

  const available = useMemo(() => {
    const sanitized = custom.map((t) => t.trim()).filter(Boolean);
    const combined = [...BUILTIN_ENCOUNTER_TYPES, ...sanitized];
    const seen = new Set<string>();
    return combined.filter((t) => {
      const k = t.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [custom]);

  const createType = useCallback(
    async (name: string) => {
      if (!session || !clinicId) throw new Error("Missing clinic/session");
      const trimmed = name.trim();
      if (!trimmed) throw new Error("Type name required");
      const created = await createCustomEncounterType(
        session,
        trimmed,
        clinicId,
      );
      const clean = created?.name?.trim();
      if (clean) setCustom((prev) => [...prev, clean]);
      return clean;
    },
    [session, clinicId],
  );

  return { available, loading, error, reload: load, createType };
}
